import { i18n } from '../index'
import type { FormattedNumbers } from '../types/i18n'

export const useFormatters = (): FormattedNumbers => {
  const { t, locale } = i18n.global

  const currency = (amount: number, currencyCode: string = 'AED'): string => {
    const localeToUse = locale.value.startsWith("ar")
      ? `${locale.value}-u-nu-arab`
      : locale.value;

    const formatter = new Intl.NumberFormat(localeToUse, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    
    return formatter.format(amount)
  }

  const number = (num: number, decimals: number = 2): string => {
    const formatter = new Intl.NumberFormat(locale.value, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })
    
    return formatter.format(num)
  }

  const percentage = (value: number): string => {
    const formatter = new Intl.NumberFormat(locale.value, {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
    
    return formatter.format(value / 100)
  }

  const date = (date: Date, options: Intl.DateTimeFormatOptions = {}): string => {
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    }
    
    const formatter = new Intl.DateTimeFormat(locale.value, defaultOptions)
    return formatter.format(date)
  }

  const relativeTime = (timestamp: number): string => {
    const now = Date.now()
    const diff = now - timestamp
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return t('common.time.justNow')
    if (minutes < 60) return t('common.time.minutesAgo', { count: minutes })
    if (hours < 24) return t('common.time.hoursAgo', { count: hours })
    return t('common.time.daysAgo', { count: days })
  }

  return {
    currency,
    number,
    percentage,
    date,
    relativeTime
  }
}